import Chart, { useChart } from 'src/components/chart';
import { useTheme } from '@mui/material/styles';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useLocales } from 'src/locales';
import { formatCurrency } from 'src/utils/format-currency';
import { formatNumber } from 'src/utils/format-number';
import { Box } from '@mui/system';

type Props = {
    data: {
        date: string;
        ad_cost: number;
        savings: number;
        brand_traffic: number;
    }[];
};

export default function ChartPerformanceBusinessOverview({ data }: Props) {
    const theme = useTheme();
    const { t } = useLocales();
    const workspace = useCurrentWorkspace();
    const lang = localStorage.getItem('i18nextLng') || 'en';

    const labels = (data || []).map((point) => {
        if (!point.date) return '';
        const d = new Date(point.date);
        return isNaN(d.getTime()) ? point.date : d.toISOString().split('T')[0];
    });

    const Y_AXIS_MARGIN_MULTIPLIER = 1.3;

    // arrondir max sur un intervalle "propre"
    const roundUpToNiceInterval = (value: number): number => {
        if (value <= 0) return 1;
        const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
        const normalized = value / magnitude;
        let niceValue;
        if (normalized <= 1.2) niceValue = 1.2;
        else if (normalized <= 1.5) niceValue = 1.5;
        else if (normalized <= 2) niceValue = 2;
        else if (normalized <= 3) niceValue = 3;
        else if (normalized <= 5) niceValue = 5;
        else if (normalized <= 7) niceValue = 7;
        else niceValue = 10;
        return niceValue * magnitude;
    };

    // calcul du max stacké (ad_cost + savings)
    const calculateMaxAdCostSavings = (data: Props['data']): number => {
        const stackedValues = data.map((d) => (d.ad_cost || 0) + (d.savings || 0));
        const maxVal = Math.max(...stackedValues, 0);
        return roundUpToNiceInterval(maxVal * Y_AXIS_MARGIN_MULTIPLIER);
    };

    const maxBars = calculateMaxAdCostSavings(data);

    const series = [
        {
            name: "Brand Traffic",
            type: "line",
            data: data?.map((point) => point.brand_traffic) || [],
        },
        {
            name: t("dashboard.global.ad_cost"),
            type: "bar",
            data: data?.map((point) => point.ad_cost) || [],
        },
        {
            name: t("dashboard.global.savings"),
            type: "bar",
            data: data?.map((point) => point.savings) || [],
        },
    ];

    const chartOptions = useChart({
        // Round legend dots, smaller than the global default. In apexcharts 3.51 the legend
        // marker is an SVG shape sized by `legend.markers.size` (radius) — size 3 → 6px circle.
        // `size` isn't in the v4 typings (runtime is 3.51) → cast.
        legend: {
            markers: ({
                shape: 'circle',
                size: 3,
                strokeWidth: 0,
            } as any),
        },
        plotOptions: {
            bar: {
                columnWidth: '50%',
                borderRadius: 4,
                borderRadiusApplication: 'end',
                borderRadiusWhenStacked: 'last',
                dataLabels: { position: 'top' }
            }
        },
        chart: {
            stacked: true,
            animations: { enabled: true, speed: 500 },
            toolbar: { show: false },
        },
        fill: {
            type: ['solid', 'solid', 'solid'],
            gradient: {
                shadeIntensity: 1,
                inverseColors: false,
                opacityFrom: 0.45,
                opacityTo: 0.05,
                stops: [20, 100, 100],
            },
        },
        stroke: { width: [1.5, 0, 0], curve: 'smooth' },
        colors: [
            '#637381', // Brand Traffic
            theme.palette.warning.light,  // Ad Cost
            theme.palette.success.light,  // Savings
        ],
        labels,
        yaxis: [
            {
                // Brand Traffic
                title: { text: t('dashboard.brandTraffic.totalBrandTraffic') },
                labels: { formatter: (val: number) => formatNumber(val) },
                min: 0
            },
            {
                // Ad Cost + Savings (stackés)
                opposite: true,
                min: 0,
                max: maxBars,
                title: { text: `${t('dashboard.global.ad_cost')} / ${t('dashboard.global.savings')}` },
                labels: {
                    formatter: (val: number) =>
                        formatCurrency(val, workspace?.currency || 'USD', lang, true, 2),
                },
            },
        ],
        tooltip: {
            shared: true,
            intersect: false,
            custom: function ({ series, dataPointIndex, w }: any) {
                if (!series || !w.globals.seriesNames || !w.globals.colors) {
                    return '';
                }

                // Use the original ISO labels from the component scope: ApexCharts
                // re-categorises w.globals.labels (which can collapse to just the day).
                const date = labels[dataPointIndex] || w.globals.labels[dataPointIndex] || '';

                // Read values from the original data (not from `series`): the savings
                // series nulls out zeros for the border-radius, but the tooltip must
                // still show $0.00. Index order matches the series array above.
                const point = data?.[dataPointIndex] || {};
                const valuesByIndex = [point.brand_traffic, point.ad_cost, point.savings];

                const rows = w.globals.seriesNames
                    .map((name: string, i: number) => ({
                        name,
                        color: w.globals.colors[i],
                        value: valuesByIndex[i],
                    }))
                    // Brand Traffic (line) is rendered last to match the design
                    .sort((a: any, b: any) => Number(a.name === 'Brand Traffic') - Number(b.name === 'Brand Traffic'))
                    .map(({ name, color, value }: any) => {
                        const formatted = value == null || isNaN(value)
                            ? '—'
                            : name === 'Brand Traffic'
                                ? formatNumber(value)
                                : formatCurrency(value, workspace?.currency || 'USD', lang, true, 2);
                        return `
                            <div style="display:flex;align-items:center;gap:8px;padding-left:4px;">
                                <span style="display:inline-block;width:2px;height:12px;border-radius:2px;background-color:${color};flex-shrink:0;"></span>
                                <div style="flex:1;display:flex;justify-content:space-between;align-items:center;font-family:Public Sans,sans-serif;font-size:13px;font-weight:500;color:#333c47;white-space:nowrap;gap:16px;">
                                    <span>${name}</span>
                                    <span>${formatted}</span>
                                </div>
                            </div>`;
                    })
                    .join('');

                // Même design que le tooltip Explore (cf. ExploreChart) : conteneur propre
                // (.cb-clean-tooltip neutralise le cadre apex par défaut), séparation date/données
                // par l'espacement, pas de trait.
                return `
                    <div class="cb-clean-tooltip" style="background:#fcfcfd;border:1px solid rgba(42,50,59,0.2);border-radius:12px;box-shadow:1px 2px 10px 0 rgba(0,0,0,0.06);overflow:hidden;min-width:240px;">
                        <div style="padding:12px 12px 0 12px;">
                            <p style="font-family:Public Sans,sans-serif;font-size:14px;font-weight:700;color:#333c47;white-space:nowrap;margin:0;">${date}</p>
                        </div>
                        <div style="padding:12px;display:flex;flex-direction:column;gap:6px;">${rows}</div>
                    </div>`;
            },
        },
    });


    return (
        <Box sx={{ minHeight: 0, width: '100%', height: '100%' }}>
            <Chart
                dir="ltr"
                type="line"
                height={'100%'}
                series={series}
                options={chartOptions}
                // ApexCharts v4.7 groups combo-chart series in the legend (here the two
                // stacked bars, Ad Cost + Savings, land in one group) and, for top/bottom
                // legends, renders each group `flex-direction: column-reverse` via
                // `.apexcharts-legend-group-vertical` — stacking those two items onto a
                // second line. Force the groups back to a row so all series sit on one
                // line. Scoped to this chart via the root class so the shared legend CSS
                // other multi-series charts rely on is untouched.
                sx={{
                    mt: 3,
                    '& .apexcharts-legend-group-vertical': {
                        flexDirection: 'row',
                    },
                }}
            />
        </Box>

    );
}
